import React from 'react';
import { FormattedMessage } from 'react-intl';
import CodeInput from 'react-otp-input';
import { useDispatch } from 'react-redux';

import { useSelector } from 'hooks/useSelector';
import { setAuthStep, verifySignIn } from 'modules/auth/actions';
import { LOGIN_STEPS } from 'modules/auth/constant';
import {
  getMFAEmail,
  getLoginUsername,
  hasVerifySignInError,
  getVerifySignInLoading,
  isAccountLocked as isAccountLockedSelector, getRemainingAttempt
} from 'modules/auth/selectors';
import { Alert, Button, Card } from 'ui/base';

import AccountLockWrapper from './AccountLockWrapper';
import ResendWrapper from './ResendWrapper';
import { Footer, VerifyContent, Wrapper, StyledInput, containerStyle, errorStyle } from './UI';

const NUM_INPUTS = 6;

const VerifyStep = () => {
  const dispatch = useDispatch();

  const email = useSelector(getMFAEmail);
  const username = useSelector(getLoginUsername);
  const isVerifyLoading = useSelector(getVerifySignInLoading);
  const hasVerifyError = useSelector(hasVerifySignInError);
  const remainingAttempt = useSelector(getRemainingAttempt);
  const isAccountLocked = useSelector(isAccountLockedSelector);

  const submitRef = React.useRef(undefined);

  const [hasError, setHasError] = React.useState();
  const [verifyCode, setVerifyCode] = React.useState();

  React.useEffect(() => setHasError(hasVerifyError && !isAccountLocked), [hasVerifyError, isAccountLocked]);

  const handleSubmit = React.useCallback(() => dispatch(verifySignIn(verifyCode)), [dispatch, verifyCode]);

  const handleBack = React.useCallback(
    () => dispatch(setAuthStep(LOGIN_STEPS.LOGIN, { username })),
    [dispatch, username]
  );

  const handleChange = React.useCallback(code => {
    setVerifyCode(code);
    setHasError(false);
  }, []);

  const renderInput = React.useCallback(props => <StyledInput disabled={isAccountLocked} data-testId="otp-input" {...props} />, [isAccountLocked]);

  const disabled = verifyCode?.length !== NUM_INPUTS;
  const displayAttemptWarning = remainingAttempt <= 5 && !isAccountLocked;

  React.useLayoutEffect(() => {
    if (verifyCode?.length === NUM_INPUTS) {
      submitRef.current.focus();
    }
  }, [verifyCode]);

  return (
    <Wrapper className="ant-form-vertical">
      <Card title={<FormattedMessage id="login" defaultMessage="Login" />} variant="borderless">
        <VerifyContent>
          <span className="title">
            <FormattedMessage id="page.login.mfa.title" defaultMessage="Two-factor authentication" />
          </span>
          <span className="text-center">
            <FormattedMessage
              id="page.login.mfa.description"
              defaultMessage="A message with a verification code has been sent to your mail {email}. Please enter the code to continue."
              values={{ email }}
            />
          </span>
          <div>
            <CodeInput
              inputType="tel"
              shouldAutoFocus
              value={verifyCode}
              numInputs={NUM_INPUTS}
              onChange={handleChange}
              renderInput={renderInput}
              containerStyle={containerStyle}
            />

            {hasError && (
              <p className={errorStyle}>
                <FormattedMessage
                  id="page.login.mfa.invalidOTP"
                  defaultMessage="The authentication code is not valid"
                />
              </p>
            )}
          </div>
          {!isVerifyLoading && <ResendWrapper />}
          {isAccountLocked && <AccountLockWrapper />}
          {displayAttemptWarning && (
            <Alert
              type="warning"
              title={
                <FormattedMessage
                  values={{ remainingAttempt }}
                  id="page.login.mfa.warningRemainingAttempt"
                  defaultMessage="Only {remainingAttempt} login attempts left."
                />
              }
            />
          )}
        </VerifyContent>
        <Footer className="d-flex justify-content-between">
          <Button type="default" onClick={handleBack} data-testid="back-btn">
            <FormattedMessage id="back" defaultMessage="Back" />
          </Button>
          <Button
            type="primary"
            ref={submitRef}
            onClick={handleSubmit}
            data-testid="submit-btn"
            loading={isVerifyLoading}
            disabled={isVerifyLoading || disabled}
          >
            <FormattedMessage id="login" defaultMessage="Login" />
          </Button>
        </Footer>
      </Card>
    </Wrapper>
  );
};

export default VerifyStep;
